/**
 * Glass Achievement Card
 *
 * A stunning 3D glassmorphism card with holographic effects
 * for the Phase 4 celebration achievement display.
 */

import { useRef, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { AutoAwesome } from '@mui/icons-material';
import '../styles/genesis-event.css';

interface GlassCardProps {
  /** Whether the card is visible */
  visible: boolean;
  /** Callback when card animation completes */
  onAnimationComplete?: () => void;
}

const GlassCard = ({ visible, onAnimationComplete }: GlassCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });

  // Track mouse for 3D tilt effect
  useEffect(() => {
    if (!visible) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!cardRef.current) return;

      const rect = cardRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      setMousePos({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [visible]);

  const rotateX = (mousePos.y - 0.5) * -15;
  const rotateY = (mousePos.x - 0.5) * 15;

  return (
    <AnimatePresence>
      {visible && (
        /* Full-screen centering layer (no motion) */
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
            pointerEvents: 'none',
          }}
        >
          {/* Perspective wrapper with entrance animation */}
          <Box
            component={motion.div}
            initial={{ scale: 0, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: -60 }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 20,
              delay: 0.2,
            }}
            onAnimationComplete={onAnimationComplete}
            sx={{ perspective: '1000px' }}
          >
            <Box
              ref={cardRef}
              className="genesis-glass-card"
              component={motion.div}
              animate={{ rotateX, rotateY }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              sx={{
                minWidth: { xs: '300px', sm: '420px' },
                maxWidth: '90vw',
                textAlign: 'center',
                transformStyle: 'preserve-3d',
                pointerEvents: 'auto',
              }}
            >
              {/* Main text content */}
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6, ease: 'easeOut' }}
                sx={{ textAlign: 'center' }}
              >
                {/* "You did it!" with emoji */}
                <Typography
                  variant="h3"
                  component={motion.h3}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    delay: 0.5,
                    type: 'spring',
                    stiffness: 200,
                    damping: 15,
                  }}
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 'clamp(2rem, 6vw, 3.25rem)',
                    fontWeight: 900,
                    color: 'rgba(0, 0, 0, 0.87)',
                    mb: 1.5,
                    lineHeight: 1.2,
                  }}
                >
                  You did it! 🎉
                </Typography>

                {/* Subtitle */}
                <Typography
                  component={motion.p}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0, duration: 0.5 }}
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 'clamp(1.1rem, 3vw, 1.35rem)',
                    fontWeight: 500,
                    color: 'rgba(0, 0, 0, 0.6)',
                    lineHeight: 1.5,
                    maxWidth: 300,
                    mx: 'auto',
                  }}
                >
                  Your first video is ready to share
                </Typography>

                {/* Sparkle accent */}
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.3, type: 'spring', stiffness: 200, damping: 12 }}
                  sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 0.5 }}
                >
                  {[0, 1, 2].map((i) => (
                    <AutoAwesome
                      key={i}
                      component={motion.svg}
                      animate={{
                        opacity: [0.4, 1, 0.4],
                        scale: [0.8, 1.1, 0.8],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        delay: i * 0.3,
                        ease: 'easeInOut',
                      }}
                      sx={{
                        fontSize: 18,
                        color: ['var(--genesis-primary)', 'var(--genesis-accent)', 'var(--genesis-secondary)'][i],
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default GlassCard;
